import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://sb.impultienda.ar/storage/v1/object/public/ebook-images/33e96a16-a8c4-4e84-bf63-ae019c15411e/product-uploads/mcp_1709bee6-b7e9-4ee7-989f-d7074950c4d0_1793811811896.webp';
export const heroGuarantees = ['Øjeblikkelig digital adgang', 'Engangsbetaling', 'Intet abonnement', '7 dages tilfredshedsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i de seks bind'
}, {
  value: '6',
  label: 'Digitale bind med vandgymnastik'
}, {
  value: '3',
  label: 'Hold – blid, aquafitness og aquabike'
}, {
  value: '5',
  label: 'Bonusser inkluderet'
}, {
  value: '1',
  label: 'Komplet system til bassinet'
}];
export const differenceStats = [{
  value: '3 hold',
  label: 'Fra blid vandgymnastik til aquabike, på tre niveauer'
}, {
  value: '6 digitale bind',
  label: 'Fra sæsonplanen til fremskridtsmærkerne'
}, {
  value: 'PDF til print',
  label: 'På tablet eller printet – med ud på bassinkanten'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Søndagsaftenerne forsvinder',
  text: 'Timevis foran computeren med at forberede ugens holdtimer – og du er stadig ikke færdig.'
}, {
  title: 'Timevis med at lede',
  text: 'Øvelserne fra internettet er for lette, for svære eller passer ikke til dit hold.'
}, {
  title: 'Hver holdtime bygges fra bunden',
  text: 'Opvarmning, grundbevægelser, hoveddel, fællesleg, nedkøling – det, der burde tage minutter, bliver et rigtigt stykke arbejde.'
}, {
  title: 'Svært at håndtere forskellige niveauer',
  text: 'Begyndere og rutinerede i samme bassin: der er altid nogen, der keder sig.'
}, {
  title: 'Opfølgning og organisering tager tid',
  text: 'Fremmødelister, mærker, beskeder til deltagerne: endnu mere arbejde efter holdtimen.'
}, {
  title: 'Stress før holdtimen',
  text: 'Når tiden er knap, improviserer man og finder på noget i sidste øjeblik på bassinkanten.'
}];
export const afterItems = ['En organiseret vandgympakke til voksne i alle aldre', 'Holdtimer minut for minut, der er nemme at følge', 'Holdtimekort, du kan printe med det samme', '100 færdige øvelser i vandgymnastik og aquabike', 'Bevægelserne trin for trin og fremskridtsmærkerne', '3 niveauer i samme bassin', 'Klare sikkerhedsregler på bassinkanten', 'Færdige opfølgningsskemaer og fremskridtstest', 'En plan for hele sæsonen'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Søndagene går med forberedelse',
  text: 'Bruger du en del af weekenden på at forberede ugens holdtimer?'
}, {
  icon: FolderSearchIcon,
  title: 'Du ved ikke længere, hvilken øvelse du skal vælge',
  text: 'Leder du hele tiden efter bevægelser, der passer til dine deltageres niveau?'
}, {
  icon: LightbulbIcon,
  title: 'Du vil gerne forklare tingene tydeligere',
  text: 'Har du brug for visuelt materiale, så du nemt kan vise en bevægelse fra bassinkanten?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vil holde op med at lave alt fra bunden',
  text: 'Kunne du tænke dig, at holdtimerne, øvelserne og kortene allerede var klar?'
}, {
  icon: ShuffleIcon,
  title: 'Dine deltagere har brug for variation',
  text: 'Vil du have flere koreografier, lege og stationer, der holder holdet motiveret?'
}, {
  icon: LayoutGridIcon,
  title: 'Du har brug for en tydeligere struktur',
  text: 'Vil du have alt dit materiale samlet, så du hurtigt finder det, du skal bruge?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: 'Niveau 1–3',
  title: 'Sæsonplan & 12 holdtimer',
  tagline: 'Hele sæsonen organiseret, måned for måned.',
  intro: 'Det første bind giver dig sæsonplanen for de tre hold og 12 komplette holdtimer minut for minut.',
  items: ['Sæsonplan for blid vandgymnastik og seniorer, aquafitness og aquabike', 'Ugen med 1, 2 eller 3 holdtimer', 'Opbygning af en holdtime på 30 og 45 minutter', '3 niveauer i samme bassin', '12 holdtimer minut for minut', 'Holdtimekort til print', 'Bassinplan og stationer', 'Sikkerhed og tjekliste', 'Opfølgningsskema for holdet'],
  outro: 'Gå ud på bassinkanten og vid præcis, hvad du skal lave.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: 'Niveau 1–3',
  title: 'Timebank: 50 færdige holdtimer',
  tagline: 'En færdig holdtime til hver situation.',
  intro: 'Det andet bind samler 50 færdige holdtimer sorteret efter tema og hold.',
  items: ['Opbygning på 30 og 45 minutter', '8 holdtimer med blid vandgymnastik og seniorer', 'Mobilitet og balance', 'Blid cardio og rytme', 'Generel styrke med redskaber', 'Aquabike og aquacircuit', 'Før mærkedagen', 'Prøvetimer og dybt vand', 'Holdtimer i par'],
  outro: 'Vælg, print, kør holdet.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: 'Niveau 1–3',
  title: '100 øvelser i vandgymnastik og aquabike',
  tagline: 'Deltagere, der bevæger sig, smiler og kommer igen.',
  intro: 'Det tredje bind indeholder 100 øvelser sorteret efter mål og hold, med variationer og sikkerhed.',
  items: ['Opvarmning i vandet', 'Bevægelser uden redskaber', 'Øvelser med aquanudler og svømmeplader', 'Handvægte, vandhandsker og steps', 'Øvelser på aquabiken', 'Øvelser til blid vandgymnastik og seniorer', 'Fælleslege', 'Nedkøling', 'Lav din egen koreografi'],
  outro: 'Glæden ved vandgymnastik – uden forberedelse.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: 'Niveau 1–3',
  title: 'Bevægelser trin for trin, musik & mærker',
  tagline: 'Hver bevægelse forklaret enkelt.',
  intro: 'Det fjerde bind viser bevægelserne trin for trin, musikken i BPM og fremskridtsmærkerne.',
  items: ['Vandgang og forflytninger', 'Armbevægelser under vandet', 'Indstilling og stillinger på aquabiken', 'Musikken og vandtempoet', 'Koreografierne', 'Fremskridtsmærkerne', 'Bevægelseskort til print', 'Typiske fejl og rettelser'],
  outro: 'Tydelige bevægelser, tilpasset hvert niveau.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: 'Niveau 1–3',
  title: 'Sikkerhed, hold & deltagere',
  tagline: 'Et trygt hold og trofaste deltagere.',
  intro: 'Det femte bind hjælper dig med sikkerheden i bassinet, livet på holdet og kontakten med deltagerne.',
  items: ['Sikkerhedsregler i bassinet', 'Tegn på ubehag og hvad man gør', 'Holdledelse og ritualer', 'Utrygge deltagere og begyndere', 'Kommunikation med deltagerne', 'Tilmelding, klippekort og helbredserklæring', 'Planlægning af sæsonen', 'Tjeklister'],
  outro: 'Mere ro, holdtime efter holdtime.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: 'Niveau 1–3',
  title: 'Fremskridt, temaer & sæsonafslutning',
  tagline: 'Se hver deltager blive bedre.',
  intro: 'Det sjette bind indeholder fremskridtstest, temahold og alt, hvad du skal bruge for at afslutte sæsonen med stil.',
  items: ['Opfølgningsskemaer', 'Test af tryghed og mobilitet', 'Observationsskemaer', 'Mærkedagen', 'Temahold', 'Diplomer', 'Status på sæsonen', 'Opfølgning på holdet'],
  outro: 'Synlige fremskridt for deltagerne og svømmehallen.',
  note: 'Materialet erstatter ikke uddannelse, livredderprøve eller svømmehallens sikkerhedsinstruks.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind til dine hold',
  text: 'En komplet pakke med vandgymnastik og aquabike, så du kan forberede og lede dine holdtimer hele sæsonen. Hvert bind indeholder 40 sider med holdtimer, øvelser, kort og aktiviteter til bassinet.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Færdige holdtimer',
  text: 'Slut med det hvide papir. Holdtimer minut for minut med instruktørens ord, så du kan gå i gang med det samme.'
}, {
  icon: FileTextIcon,
  title: 'Kort til print',
  text: 'Holdtimekort, plakater og opfølgningsskemaer, du kan printe og tage med ud på bassinkanten.'
}, {
  icon: ShapesIcon,
  title: 'Bevægelser i billeder',
  text: 'Bassinplaner, illustrerede trin og enkle instrukser, så du kan forklare hver bevægelse.'
}, {
  icon: DicesIcon,
  title: 'Øvelser og lege',
  text: '100 øvelser til opvarmning, bevægelse og fællesskab, med eller uden redskaber og altid sikkert.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Fremskridt og mærker',
  text: 'Observationsskemaer, fremskridtstest og mærker, der er klar til brug.'
}, {
  icon: CalendarCheckIcon,
  title: 'Sæsonplan',
  text: 'Fra august/september til juni har hver måned sit tema, sine mål og sine begivenheder.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/33e96a16-a8c4-4e84-bf63-ae019c15411e/product-uploads/mcp_1709bee6-b7e9-4ee7-989f-d7074950c4d0_1793816071940.webp",
    "label": "Bind 01 · Side 1",
    "title": "Forsiden",
    "text": "Sæsonplan og 12 holdtimer minut for minut."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/33e96a16-a8c4-4e84-bf63-ae019c15411e/product-uploads/mcp_eb841872-3dcf-4a86-89a7-63bf05577c07_1793816074612.webp",
    "label": "Sæsonplan · Side 4",
    "title": "Blid vandgymnastik og seniorer",
    "text": "Sæsonplanen måned for måned med mål og musik i BPM."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/33e96a16-a8c4-4e84-bf63-ae019c15411e/product-uploads/mcp_c18399fb-fa34-4f9a-b4ae-ceedbb02dd2d_1793816077102.webp",
    "label": "Holdtimekort · Side 11",
    "title": "Holdtimekort 1",
    "text": "Forløbet minut for minut, de 3 niveauer og sikkerheden på én side."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/33e96a16-a8c4-4e84-bf63-ae019c15411e/product-uploads/mcp_7d31d307-7125-48e6-9969-27477147ae32_1793816080080.webp",
    "label": "Holdtimekort · Side 21",
    "title": "Holdtimekort 6",
    "text": "Udstyret, musikken og instruktørens tips, klar til print."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/33e96a16-a8c4-4e84-bf63-ae019c15411e/product-uploads/mcp_c90e3d49-110b-42f8-9beb-24322c5e07a2_1793816083363.webp",
    "label": "Stationer · Side 34",
    "title": 'Bassinplan og stationer',
    "text": "Stationerne i samme bassin med udstyr og antal deltagere."
  }
];
export const pageTypes = ['Færdige holdtimer', 'Kort til print', 'Øvelser og stationer', 'Fremskridt og mærker'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikar uden stress – 12 færdige holdtimer til vikaren',
  text: 'Holdet bliver ved med at bevæge sig, også når du ikke kan komme.',
  items: ['Overdragelsesark', '12 færdige holdtimer', '4 pr. hold', 'Fremmødeliste'],
  benefit: 'Tag roligt afsted – holdtimen bliver til noget.'
}, {
  title: 'Opvarmning på 5 minutter – 60 kort',
  text: '60 vandgym-kort til print, så du kan sætte en opvarmning sammen på fem minutter.',
  items: ['Gang og forflytninger i vandet', 'Mobilitet i skuldre og arme', 'Mobilitet i hofter og balance', '5 færdige opvarmninger'],
  benefit: 'En ny opvarmning hver holdtime.'
}, {
  title: 'Temahold og vandgymfest',
  text: 'Alt, hvad du skal bruge til at holde temahold og en sommerafslutning uden kaos.',
  items: ['Plan over 6 uger', '10 temahold', 'Diplomer til print', 'Holdtimen «tag en ven med»'],
  benefit: 'Et vellykket arrangement uden stress.'
}, {
  title: 'Kommunikation med deltagerne – 40 beskeder',
  text: 'Færdige beskeder til hele sæsonen, som du kan kopiere og sende.',
  items: ['Tilmelding og klippekort', 'Sæsonstart og holdtider', 'Aflyst eller flyttet holdtime', 'Velkomstmøde og ofte stillede spørgsmål'],
  benefit: 'Informerede og trofaste deltagere.'
}, {
  title: 'Plakater til svømmehallen – 15 plakater',
  text: 'Farverige plakater, der hjælper med at organisere holdtimen og tage imod deltagerne.',
  items: ['Bassinets regler', 'Indstilling af aquabiken', 'Instruktørens signaler', 'Hver i sit tempo'],
  benefit: 'Et bassin, der informerer, også mens du taler.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åbn VANDGYM KLAR™',
  text: 'Download pakken, og åbn det bind, der passer til dit hold.'
}, {
  title: 'Vælg holdtimen',
  text: 'Find måneden og temaet, og vælg den holdtime, øvelse eller det kort, der passer til dine deltagere.'
}, {
  title: 'Gør dig klar på 5 minutter',
  text: 'Læs forløbet, og find det udstyr og den musik frem, du skal bruge.'
}, {
  title: 'Print, og gå ud til bassinet',
  text: 'Print holdtimekortet, eller hav det på din tablet – og start med en tydelig plan.'
}];
export const audienceItems = ['Leder holdtimer i blid vandgymnastik, aquafitness eller aquabike', 'Vil have en hel sæson vandgymnastik organiseret', 'Vil holde op med at forberede hver holdtime fra bunden', 'Har forskellige niveauer i samme bassin', 'Leder efter sikre og varierede øvelser', 'Vil have færdige opfølgningsskemaer og mærker', 'Vil spare tid hver uge', 'Vil føle dig klar før hver holdtime'];
export const timeValueTasks = ['Lede efter øvelser', 'Skrive forløbet til holdtimerne', 'Forberede stationer, udstyr og musik', 'Organisere hold og niveauer', 'Følge op på fremskridt og skrive til deltagerne'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Sæsonplan, holdtimer, øvelser, bevægelser, sikkerhed og opfølgning'
}, {
  label: '5 bonusser uden ekstra omkostninger',
  detail: 'Vikar, opvarmning, temahold, beskeder til deltagerne og plakater'
}, {
  label: 'Blid vandgymnastik, aquafitness og aquabike',
  detail: 'Færdige holdtimer, kort til print og opfølgning på fremskridt'
}];
export const offerIncludes = ['Øjeblikkelig digital adgang', 'PDF-filer, du kan beholde', 'Kort og skemaer til print', 'Der sendes intet fysisk produkt'];
export const PRICE = '199 kr';
export const COMPARE_PRICE = '699 kr';

// Only add authentic reviews of VANDGYM KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'VANDGYM KLAR™ leveres digitalt. Efter købet får du adgang til PDF-filerne, som du kan downloade og beholde. Du kan bruge dem på computer, tablet eller telefon og printe de kort, du har brug for.'
}, {
  q: 'Passer VANDGYM KLAR™ til danske svømmehaller?',
  a: 'Ja. Materialet er lavet til instruktører i vandgymnastik og aquabike og til livreddere i kommunale svømmehaller, svømmeklubber, DGI-foreninger og motionscentre med bassin: lavt og dybt vand, holdtider, klippekort, holdtilmelding pr. kvartal, sæsonstart i august/september og skoleferier. Materialet erstatter ikke uddannelse, livredderprøve eller svømmehallens sikkerhedsinstruks.'
}, {
  q: 'Passer det til alle mine hold?',
  a: 'Ja. De seks bind er opdelt i tre hold: blid vandgymnastik og seniorer på lavt vand, aquafitness – tonisk vandgymnastik, aquabike og aquacircuit. Du vælger efter deltagerne og dit holds niveau.'
}, {
  q: 'Kan jeg virkelig forberede en holdtime på 5 minutter?',
  a: 'VANDGYM KLAR™ er lavet til at gøre forberedelsen hurtigere. Du åbner et bind, vælger en færdig holdtime og læser den igennem på cirka fem minutter. Tiden kan variere alt efter holdet og dine tilpasninger.'
}, {
  q: 'Er materialet nok til en hel sæson?',
  a: 'VANDGYM KLAR™ indeholder holdtimer og aktiviteter til hele sæsonen, fra august/september til juni. Du vælger, kombinerer og tilpasser efter din plan og dit hold.'
}, {
  q: 'Er der kort, der er klar til print?',
  a: 'Ja. Pakken indeholder holdtimekort, plakater, opfølgningsskemaer og diplomer til print.'
}, {
  q: 'Er øvelserne sikre for mine deltagere?',
  a: 'Ja. Ned ad trappen, passende vanddybde, drikkepauser, altid en version uden hop og en klar regel: man øger udslaget og modstanden, aldrig farten. Hver holdtime har en sikkerhedsboks.'
}, {
  q: 'Kan jeg bruge det med forskellige niveauer?',
  a: 'Ja. Hver holdtime har 3 niveauer, så begyndere og rutinerede kan bevæge sig sammen i samme bassin.'
}, {
  q: 'Skal jeg printe det hele?',
  a: 'Nej. Du printer kun de sider, du vil, og resten kan du bruge på tablet.'
}, {
  q: 'Er de fem bonusser inkluderet?',
  a: `Ja. Alle fem bonusser er inkluderet i pakken uden ekstra omkostninger – det hele for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nej. Du betaler én gang og får adgang til det digitale materiale. Der er ingen tilbagevendende betalinger.'
}, {
  q: 'Kan jeg beholde filerne?',
  a: 'Ja. Du kan downloade og beholde PDF-filerne i henhold til produktets brugsbetingelser.'
}, {
  q: 'Kan jeg bruge det på telefon eller tablet?',
  a: 'Ja. PDF-filerne kan åbnes på computer, tablet og telefon. Vi anbefaler computer eller tablet, hvis du vil tjekke layoutet, før du printer.'
}, {
  q: 'Er der en garanti?',
  a: 'Ja. VANDGYM KLAR™ har en 7 dages tilfredshedsgaranti. Hvis materialet ikke lever op til dine forventninger, kan du skrive til os inden for garantiperioden og bede om at få pengene tilbage i henhold til betingelserne.'
}];
export const footerLinks = ['Salgsbetingelser', 'Privatlivspolitik', 'Returpolitik', 'Kontakt'];
